import React, { useEffect, useRef, useState } from "react";
import "./Header.css";
import { getUser } from "../../Api/ServerInterface";
import useStore from "../../Store/Store";
import { RepoDropdown } from "./RepoDropdown";
import { BranchesDropdown } from "./BranchDropdown";
import FileMenu from "./FileMenu/FileMenu";
import DataSourceToggle from "./DataSourceToggle/DataSourceToggle";
import OptionsMenu from "./OptionsMenu/OptionsMenu";
import StatusBar from "./StatusBar/StatusBar";
import DiscordButtonSVG from "../../Media/DiscordLogo";
import { ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { theme } from "../../Themes/Themes";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { getURLParam } from "../../utils/URLUtils";
import * as Sentry from "@sentry/react";
import { logger } from "../../utils/Logger";
import { login } from "../../utils/AuthUtils";
import HelpMenu from "./HelpMenu/HelpMenu";
import { createPortal } from "react-dom";
import { ModelSelector } from "../Common/ModelSelector/ModelSelector";

export const Header: React.FC = () => {
  const discordIconRef = useRef<HTMLDivElement>(null);
  const [discordIconPosition, setDiscordIconPosition] = useState({
    top: 0,
    right: 0,
  });

  const {
    sessionMode,
    undoState,
    redoState,
    URLParamParsingDone,
    setLoggedIn,
    setUsername,
    setDialog,
    isRunningSimulation,
    selectedSimulationKey,
    isMaxSD,
    showLandingComponent,
  } = useStore((state) => ({
    sessionMode: state.session.mode,
    undoState: state.undoRedoState.undo,
    redoState: state.undoRedoState.redo,
    URLParamParsingDone: state.URLParamParsingDone,
    setLoggedIn: state.setLoggedIn,
    setUsername: state.setUsername,
    setDialog: state.setDialog,
    isRunningSimulation: state.isRunningSimulation,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    isMaxSD: state.isMaxSD,
    showLandingComponent: state.showLandingComponent,
  }));

  // Hide button when SD is maximized AND running simulation, or when landing page is showing
  const shouldHideDiscordButton =
    (isMaxSD && isRunningSimulation) || showLandingComponent;

  // Apply smaller styling when SD is maximized
  const shouldApplySmallStyling = isMaxSD;

  useEffect(() => {
    const updateDiscordIconPosition = () => {
      if (discordIconRef.current) {
        const rect = discordIconRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        setDiscordIconPosition({
          top: rect.top,
          right: viewportWidth - rect.right,
        });
      }
    };

    updateDiscordIconPosition();
    window.addEventListener("resize", updateDiscordIconPosition);
    window.addEventListener("scroll", updateDiscordIconPosition);

    return () => {
      window.removeEventListener("resize", updateDiscordIconPosition);
      window.removeEventListener("scroll", updateDiscordIconPosition);
    };
  }, [shouldApplySmallStyling]);

  const showStayTuned = !localStorage.getItem("emailEntered");

  const handleDiscordClick = () => {
    window.open("https://discord.gg/t3ezMyMPqr", "_blank").focus();
  };

  const handleUndoRedoClick = async (newValue) => {
    await useStore.getState().postToDrawioWaitForResponse({
      action: "UNDO_MANAGER_OPERATION",
      data: { undoManagerOperationName: newValue },
    });
  };

  const showReportDialog = () => {
    const error = new Error("[USER FEEDBACK]");
    Sentry.captureException(error);
  };

  // STEP 1: Retrieves user details once authenticated
  useEffect(() => {
    const isTestMode = localStorage.getItem("testMode");
    if (URLParamParsingDone && sessionMode === "github" && !isTestMode) {
      try {
        const username = localStorage.getItem("username");
        // checking for repo in the URL params is a hacky way to force login when user is redirected to codecanvas with url params
        const repoURLParam = getURLParam(window, "repo");
        if (!username && repoURLParam) {
          // store current url in local storage
          sessionStorage.setItem("redirectUrl", window.location.href);
          login();
        }
        getUser({
          username: username,
        }).then((resp: any) => {
          const userDetails = resp;
          if (!userDetails?.user) {
            if (getURLParam(window, "checkRunId")) {
              sessionStorage.setItem("redirectUrl", window.location.href);
              login();
              return;
            }
            logger.info(
              "User not logged in, username access token not retrieved."
            );
            setLoggedIn(false);
            return;
          }
          localStorage.setItem("accessToken", userDetails.user.access_token);
          localStorage.setItem("username", userDetails.user.username);
          setUsername(userDetails.user.username);
          setLoggedIn(true);
          sessionStorage.removeItem("loginAlreadyReAttempted");
          if (!userDetails.isAppInstalled) {
            window.location.assign(
              "https://github.com/apps/codecanvas-app/installations/new"
            );
          }
        });
      } catch (err) {
        console.error(err);
        setLoggedIn(false);
      }
    }
  }, [URLParamParsingDone, sessionMode, setLoggedIn, setUsername]);

  return (
    <header className="Header">
      <div className="LogoWrapper">
        <a href="/" className="LogoLink">
          <h2 className="Logo">CodeCanvas</h2>
        </a>
      </div>

      {/* Center Model Selector */}
      <div
        style={{
          position: "absolute",
          top: "0px",
          left: "calc(50% - 130px)",
          transform: "translateX(-50%)",
          zIndex: 1000,
        }}
      >
        <ModelSelector variant="header" />
      </div>

      <div className="RightSectionOfHeader">
        <ToggleButtonGroup
          color="primary"
          value={"undo"}
          exclusive
          style={{
            height: "17px",
            borderRadius: "20px",
            width: "34px",
            position: "relative",
            right: "90px",
            top: "-3px",
          }}
          // change border radio to 30px
          sx={{
            "& .MuiToggleButton-root": {
              borderRadius: "17px",
              border: `1px solid ${theme.custom.lighterPink}`,
              color: theme.custom.lighterPink,
              "&.Mui-disabled": {
                opacity: 0.7,
                border: `1px solid ${theme.custom.lighterPink}`,
              },
              "&.Mui-selected": {
                backgroundColor: "transparent",
              },
            },
            "& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected":
              {
                borderLeft: `1px solid ${theme.custom.lighterPink}`,
              },
          }}
          onChange={async (event, newValue) => {
            return await handleUndoRedoClick(newValue);
          }}
          aria-label="Platform"
        >
          <ToggleButton
            value="UNDO"
            selected
            disabled={!undoState}
            id={"undo-button"}
            style={{ marginRight: "2px" }}
          >
            <UndoIcon style={{ height: "17px", width: "17px" }} />
          </ToggleButton>
          <ToggleButton
            value="REDO"
            selected
            disabled={!redoState}
            id={"redo-button"}
            style={{ marginLeft: "2px" }}
          >
            <RedoIcon style={{ height: "17px", width: "17px" }} />
          </ToggleButton>
        </ToggleButtonGroup>
        <DataSourceToggle />
        <StatusBar />
        <RepoDropdown />
        <BranchesDropdown />
        <FileMenu />
        <OptionsMenu />
        {/* Placeholder div for Discord button to maintain position in Header */}
        <div
          ref={discordIconRef}
          className="DiscordButtonWrapper"
          style={{
            display: "flex",
            alignItems: "center",
            height: "28px",
            marginRight: "20px",
            width: "25px",
          }}
        >
          <div style={{ width: "25px", height: "25px" }} />
        </div>
        <HelpMenu />
      </div>

      {/* Portal renders the actual Discord button outside of Header's DOM hierarchy */}
      {!shouldHideDiscordButton &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: shouldApplySmallStyling
                ? `${discordIconPosition.top - 5}px`
                : `${discordIconPosition.top}px`,
              right: shouldApplySmallStyling
                ? "73px"
                : `${discordIconPosition.right}px`,
              zIndex: 9999998, // Just below help icon but above everything else
              display: "flex",
              alignItems: "center",
              height: shouldApplySmallStyling ? "20px" : "28px",
              transition: "all 0.2s ease",
            }}
          >
            <div
              onClick={handleDiscordClick}
              style={{
                position: "relative",
                width: shouldApplySmallStyling ? "20px" : "25px",
                height: shouldApplySmallStyling ? "20px" : "25px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
            >
              <DiscordButtonSVG />
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};
